"use client";

// TipTap "/" skill-mention extension: inline chip node views + a tippy.js
// suggestion popup. Ported from the private `@magnus/react` editor; the two
// host integrations became seams so this file can be published:
//   - the chip hover card renders via {@link SkillChipHoverProvider}
//     (absent → plain chip),
//   - the "Manage …" footer link comes from `options.manage`
//     (absent → footer hidden),
//   - category headers use `options.getCategoryLabel` (absent → raw key).
// Single source for npm + registry + (via shims) `@magnus/react`.
import { c as _c } from "react/compiler-runtime";
import { nodeInputRule, nodePasteRule } from "@tiptap/core";
import Mention, { type MentionNodeAttrs } from "@tiptap/extension-mention";
import type { EditorState } from "@tiptap/pm/state";
import { type NodeViewProps, NodeViewWrapper, ReactNodeViewRenderer, ReactRenderer } from "@tiptap/react";
import type { SuggestionKeyDownProps, SuggestionProps } from "@tiptap/suggestion";
import { cn } from "@workspace/ui/lib/utils";
import { SlidersHorizontalIcon } from "lucide-react";
import { createContext, Fragment, type ReactNode, type Ref, type RefObject, use, useEffect, useImperativeHandle, useRef, useState } from "react";
import tippy, { type Instance as TippyInstance } from "tippy.js";
import { MessageSkillReference } from "./chips";
import { SkillsIcon } from "./icons";
import { SKILL_REF_RE, type SkillPickerItem } from "./refs";

// ---------------------------------------------------------------------------
// Types + host seams
// ---------------------------------------------------------------------------

export type SkillMentionAttrs = MentionNodeAttrs & {
  slug?: string;
  description?: string;
};

/** Footer link of a mention suggestion popup ("Manage agent skills" etc.). */
export interface MentionManageLink {
  href: string;
  label: string;
  /** Client-side navigation for plain left-clicks; modified clicks fall
   *  through to the anchor. Defaults to `location.assign`. */
  navigate?: (href: string) => void;
}

/** A slash command listed above the skills in the "/" popup. */
export interface MentionCommand {
  description?: string;
  /** Plain text that replaces the typed `/query`, e.g. `"/compact "`. */
  insertText: string;
  label: string;
  /** Matched by prefix against the typed query, e.g. `"compact"`. */
  name: string;
}
export interface SkillMentionOptions {
  /** Offered only when the `/` starts the message, since a command is the
   *  whole message. */
  commands?: MentionCommand[];
  /** Renders the category group header. Falls back to the raw category key. */
  getCategoryLabel?: (category: string) => string;
  /** "Manage agent skills" footer link. Hidden when absent. */
  manage?: MentionManageLink;
}
export type ActiveSuggestion = {
  component: ReactRenderer<SuggestionListHandle>;
  query: string;
  command: (attrs: SkillMentionAttrs) => void;
  commands: MentionCommand[];
  runCommand: (command: MentionCommand) => void;
};

/**
 * Host seam for the chip hover card. Chips render inside the editor's React
 * tree, so context works here (unlike the detached suggestion popup). The
 * private runtime wraps editors with its tRPC-backed skill card; published
 * consumers get the plain chip by default.
 */
const SkillChipHoverContext = createContext<((skillId: string, chip: ReactNode) => ReactNode) | null>(null);
export function SkillChipHoverProvider(t0) {
  const $ = _c(3);
  const {
    children,
    render
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== render) {
    t1 = <SkillChipHoverContext value={render}>{children}</SkillChipHoverContext>;
    $[0] = children;
    $[1] = render;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function toMentionAttrs(item: SkillPickerItem): SkillMentionAttrs {
  return {
    id: item.skillId,
    label: item.skillName,
    slug: item.skillSlug,
    description: item.skillDescription
  };
}

/**
 * TipTap types `clientRect` as nullable at both levels; tippy requires a
 * non-null getter. Returns `null` when the suggestion has no anchor (popup
 * should not open), otherwise a getter that falls back to an empty rect if
 * the anchor disappears between frames.
 */
export function suggestionClientRect(clientRect: (() => DOMRect | null) | null | undefined): (() => DOMRect) | null {
  if (!clientRect) {
    return null;
  }
  return () => clientRect() ?? new DOMRect();
}

/**
 * Commands whose name starts with `query` (or whose label contains it).
 * Empty unless the `/` sits at the start of the document (position 1, just
 * inside the first paragraph).
 */
export function matchCommands(commands: readonly MentionCommand[] | undefined, query: string, triggerPos: number): MentionCommand[] {
  if (!commands?.length || triggerPos !== 1) {
    return [];
  }
  const q = query.toLowerCase();
  return commands.filter(c => c.name.startsWith(q) || c.label.toLowerCase().includes(q));
}
export function isSuggestionActive(state: EditorState): boolean {
  return state.plugins.some(plugin => {
    const ps = plugin.getState(state);
    return ps?.active === true;
  });
}

// ---------------------------------------------------------------------------
// Inline chip rendered inside the editor
// ---------------------------------------------------------------------------

function SkillChipView(t0) {
  const $ = _c(10);
  const {
    node,
    deleteNode,
    editor
  } = t0;
  const attrs = node.attrs as SkillMentionAttrs;
  const renderHover = use(SkillChipHoverContext);
  const skillId = attrs.id ?? "";
  const removable = editor.isEditable;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SkillsIcon />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = removable ? deleteNode : undefined;
  const t3 = attrs.label ?? "";
  let t4;
  if ($[1] !== t2 || $[2] !== t3) {
    t4 = <MessageSkillReference className="mx-0.5 inline-flex align-middle" icon={t1} onRemove={t2} skillName={t3} />;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const chip = t4;
  let t5;
  if ($[4] !== chip || $[5] !== renderHover || $[6] !== skillId) {
    t5 = skillId && renderHover ? renderHover(skillId, chip) : chip;
    $[4] = chip;
    $[5] = renderHover;
    $[6] = skillId;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <NodeViewWrapper as="span" className="inline">{t5}</NodeViewWrapper>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}

// ---------------------------------------------------------------------------
// Suggestion dropdown
// ---------------------------------------------------------------------------

type SuggestionListProps = {
  items: SkillPickerItem[];
  isLoading?: boolean;
  command: (attrs: SkillMentionAttrs) => void;
  commands: MentionCommand[];
  runCommand: (command: MentionCommand) => void;
  getCategoryLabel?: (category: string) => string;
  /** Footer link; the popup renders in a detached tippy root outside React
   *  context, so the manage seam is threaded as plain data. */
  manage?: MentionManageLink;
};
type SuggestionListHandle = {
  onKeyDown: (props: {
    event: KeyboardEvent;
  }) => boolean;
};
function SuggestionList(t0) {
  const $ = _c(33);
  const {
    ref,
    items,
    isLoading,
    command,
    commands,
    runCommand,
    getCategoryLabel,
    manage
  } = t0;
  const [selectedIndex, setSelectedIndex] = useState(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const itemRefs = useRef(t1);
  const entryCount = commands.length + items.length;
  let t2;
  if ($[1] !== command || $[2] !== commands || $[3] !== items || $[4] !== runCommand) {
    t2 = index => {
      const cmd = commands[index];
      if (cmd) {
        runCommand(cmd);
        return;
      }
      const item = items[index - commands.length];
      if (item) {
        command(toMentionAttrs(item));
      }
    };
    $[1] = command;
    $[2] = commands;
    $[3] = items;
    $[4] = runCommand;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const choose = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setSelectedIndex(0);
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== commands || $[8] !== items) {
    t4 = [items, commands];
    $[7] = commands;
    $[8] = items;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[10] !== selectedIndex) {
    t5 = () => {
      const el = itemRefs.current[selectedIndex];
      if (!el) {
        return;
      }
      const container = el.parentElement;
      if (!container) {
        return;
      }
      const itemRect = el.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      if (itemRect.top < containerRect.top + 32) {
        container.scrollTop = container.scrollTop - (containerRect.top + 32 - itemRect.top);
      } else {
        if (itemRect.bottom > containerRect.bottom - 32) {
          container.scrollTop = container.scrollTop + (itemRect.bottom - (containerRect.bottom - 32));
        }
      }
    };
    t6 = [selectedIndex];
    $[10] = selectedIndex;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] !== choose || $[14] !== entryCount || $[15] !== selectedIndex) {
    t7 = () => ({
      onKeyDown: t8 => {
        const {
          event
        } = t8;
        if (event.key === "ArrowUp") {
          if (entryCount > 0) {
            setSelectedIndex(i => (i + entryCount - 1) % entryCount);
          }
          return true;
        }
        if (event.key === "ArrowDown") {
          if (entryCount > 0) {
            setSelectedIndex(i_0 => (i_0 + 1) % entryCount);
          }
          return true;
        }
        if (event.key === "Enter" || event.key === "Tab") {
          choose(selectedIndex);
          return true;
        }
        if (event.key === "Escape") {
          return true;
        }
        return false;
      }
    });
    $[13] = choose;
    $[14] = entryCount;
    $[15] = selectedIndex;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  useImperativeHandle(ref, t7);
  let t8;
  if ($[17] !== selectedIndex) {
    t8 = index_0 => cn("flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors", index_0 === selectedIndex ? "bg-accent text-accent-foreground" : "hover:bg-accent/50");
    $[17] = selectedIndex;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const entryClass = t8;
  let t9;
  if ($[19] !== command || $[20] !== commands || $[21] !== entryClass || $[22] !== entryCount || $[23] !== getCategoryLabel || $[24] !== isLoading || $[25] !== items || $[26] !== runCommand) {
    t9 = entryCount > 0 ? <div className="max-h-72 overflow-y-auto overscroll-contain p-1 [scrollbar-width:thin]">{commands.length > 0 && <div className="px-2 pt-2 pb-1 font-medium text-[10px] text-muted-foreground/70 uppercase tracking-wide">Commands</div>}{commands.map((cmd_0, index_1) => <button className={entryClass(index_1)} key={cmd_0.name} onClick={() => runCommand(cmd_0)} ref={el_0 => {
        itemRefs.current[index_1] = el_0;
      }} title={cmd_0.description} type="button"><span className="truncate">{cmd_0.label}</span><span className="ml-auto shrink-0 font-mono text-muted-foreground text-xs">/{cmd_0.name}</span></button>)}{items.map((item_0, itemIndex) => {
        const index_2 = commands.length + itemIndex;
        const prevItem = itemIndex > 0 ? items[itemIndex - 1] : null;
        const isNewCategory = item_0.category !== prevItem?.category;
        return <Fragment key={item_0.skillId}>{isNewCategory && <div className="px-2 pt-2 pb-1 font-medium text-[10px] text-muted-foreground/70 uppercase tracking-wide">{getCategoryLabel?.(item_0.category) ?? item_0.category}</div>}<button className={entryClass(index_2)} onClick={() => command(toMentionAttrs(item_0))} ref={el_1 => {
            itemRefs.current[index_2] = el_1;
          }} type="button"><span className="truncate">{item_0.skillName}</span></button></Fragment>;
      })}</div> : <div className="p-2"><p className="px-1 text-muted-foreground text-xs">{isLoading ? "Loading skills\u2026" : "No skills found"}</p></div>;
    $[19] = command;
    $[20] = commands;
    $[21] = entryClass;
    $[22] = entryCount;
    $[23] = getCategoryLabel;
    $[24] = isLoading;
    $[25] = items;
    $[26] = runCommand;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== manage) {
    t10 = manage && <div className="border-t p-1"><a className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-accent/50 hover:text-accent-foreground" href={manage.href} onClick={e => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
          return;
        }
        e.preventDefault();
        (manage.navigate ?? _temp)(manage.href);
      }}><SlidersHorizontalIcon className="size-4 shrink-0" />{manage.label}</a></div>;
    $[28] = manage;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t9) {
    t11 = <div className="min-w-[240px] max-w-[320px] rounded-lg border bg-popover shadow-md">{t9}{t10}</div>;
    $[30] = t10;
    $[31] = t9;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
}

// ---------------------------------------------------------------------------
// Suggestion renderer factory (tippy.js — portals to body for z-index)
// ---------------------------------------------------------------------------
function _temp(href) {
  return location.assign(href);
}
function createSuggestionRenderer(skillsRef: RefObject<SkillPickerItem[]>, loadingRef: RefObject<boolean>, activeSuggestionRef: RefObject<ActiveSuggestion | null>, optionsRef: RefObject<SkillMentionOptions | undefined>, placement: "top-start" | "bottom-start") {
  const listProps = () => ({
    isLoading: loadingRef.current,
    getCategoryLabel: optionsRef.current?.getCategoryLabel,
    manage: optionsRef.current?.manage
  });
  const commandProps = (props: SuggestionProps<SkillPickerItem>) => ({
    commands: matchCommands(optionsRef.current?.commands, props.query, props.range.from),
    runCommand: (cmd: MentionCommand) => {
      props.editor.chain().focus().insertContentAt(props.range, {
        type: "text",
        text: cmd.insertText
      }).run();
    }
  });
  return {
    char: "/",
    allowedPrefixes: [" "],
    items: ({
      query
    }: {
      query: string;
    }) => {
      const q = query.toLowerCase();
      const filtered = (skillsRef.current ?? []).filter(s => s.skillName.toLowerCase().includes(q) || (s.skillDescription ?? "").toLowerCase().includes(q));
      // Cap at 50 to stay snappy; the list scrolls if this actually hits.
      return filtered.slice(0, 50);
    },
    render: () => {
      let component: ReactRenderer<SuggestionListHandle> | null = null;
      let tippyInstance: TippyInstance | null = null;
      return {
        onStart: (props: SuggestionProps<SkillPickerItem>) => {
          const cmdProps = commandProps(props);
          component = new ReactRenderer(SuggestionList, {
            props: {
              items: props.items,
              command: props.command,
              ...cmdProps,
              ...listProps()
            },
            editor: props.editor
          });
          activeSuggestionRef.current = {
            component,
            query: props.query,
            command: props.command,
            ...cmdProps
          };
          const getRect = suggestionClientRect(props.clientRect);
          if (!getRect) {
            return;
          }
          const [instance] = tippy("body", {
            getReferenceClientRect: getRect,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: "manual",
            placement,
            maxWidth: "none"
          });
          tippyInstance = instance ?? null;
        },
        onUpdate: (props: SuggestionProps<SkillPickerItem>) => {
          const cmdProps = commandProps(props);
          component?.updateProps({
            items: props.items,
            command: props.command,
            ...cmdProps,
            ...listProps()
          });
          if (activeSuggestionRef.current) {
            activeSuggestionRef.current.query = props.query;
            activeSuggestionRef.current.command = props.command;
            activeSuggestionRef.current.commands = cmdProps.commands;
            activeSuggestionRef.current.runCommand = cmdProps.runCommand;
          }
          const getRect = suggestionClientRect(props.clientRect);
          if (getRect) {
            tippyInstance?.setProps({
              getReferenceClientRect: getRect
            });
          }
        },
        onKeyDown: (props: SuggestionKeyDownProps) => {
          if (props.event.key === "Escape") {
            tippyInstance?.hide();
            return true;
          }
          return component?.ref?.onKeyDown(props) ?? false;
        },
        onExit: () => {
          activeSuggestionRef.current = null;
          tippyInstance?.destroy();
          tippyInstance = null;
          component?.destroy();
          component = null;
        }
      };
    }
  };
}

// ---------------------------------------------------------------------------
// Hook — creates a fully configured skill mention extension
// ---------------------------------------------------------------------------

export function useSkillMention(availableSkills: SkillPickerItem[], skillsLoading: boolean, placement: "top-start" | "bottom-start" = "bottom-start", options?: SkillMentionOptions) {
  const skillsRef = useRef<SkillPickerItem[]>(availableSkills);
  skillsRef.current = availableSkills;
  const loadingRef = useRef(skillsLoading);
  loadingRef.current = skillsLoading;
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const activeSuggestionRef = useRef<ActiveSuggestion | null>(null);
  const extension = useRef(Mention.extend({
    addAttributes() {
      return {
        ...this.parent?.(),
        slug: {
          default: null,
          parseHTML: (el: HTMLElement) => el.getAttribute("data-slug")
        },
        description: {
          default: null,
          parseHTML: (el_0: HTMLElement) => el_0.getAttribute("data-description")
        }
      };
    },
    addNodeView() {
      return ReactNodeViewRenderer(SkillChipView);
    },
    // Typing or pasting `{{skill:id|name}}` in any editor that uses
    // SkillMention converts to a chip. The regex is the shared
    // SKILL_REF_RE so there's one canonical format everywhere.
    addInputRules() {
      return [nodeInputRule({
        find: new RegExp(`${SKILL_REF_RE.source}$`),
        type: this.type,
        getAttributes: match => ({
          id: match[1] ?? "",
          label: match[2] ?? ""
        })
      })];
    },
    addPasteRules() {
      return [nodePasteRule({
        find: new RegExp(SKILL_REF_RE.source, "g"),
        type: this.type,
        getAttributes: match_0 => ({
          id: match_0[1] ?? "",
          label: match_0[2] ?? ""
        })
      })];
    }
  }).configure({
    deleteTriggerWithBackspace: true,
    HTMLAttributes: {
      class: "skill-mention"
    },
    suggestion: createSuggestionRenderer(skillsRef, loadingRef, activeSuggestionRef, optionsRef, placement)
  })).current;
  useEffect(() => {
    const active = activeSuggestionRef.current;
    if (!active) {
      return;
    }
    const q = active.query.toLowerCase();
    const items = availableSkills.filter(s => s.skillName.toLowerCase().includes(q)).slice(0, 8);
    active.component.updateProps({
      items,
      isLoading: skillsLoading,
      command: active.command,
      commands: active.commands,
      runCommand: active.runCommand,
      getCategoryLabel: optionsRef.current?.getCategoryLabel,
      manage: optionsRef.current?.manage
    });
  }, [availableSkills, skillsLoading]);
  return {
    extension,
    activeSuggestionRef
  };
}